import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
} from "@mui/material";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../utils/api";

const ALLOWED_PALETTE_KEYS = new Set([
  "primary",
  "secondary",
  "error",
  "warning",
  "info",
  "success",
]);

const ITEMS_PER_PAGE = 10;

const normalizePaletteKey = (value, fallback = "secondary") => {
  const key = String(value || "").toLowerCase().trim();
  if (key === "inherit") return fallback;
  return ALLOWED_PALETTE_KEYS.has(key) ? key : fallback;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString();
};

const ArchitecturalRequestByMember = ({ propertyId }) => {
  const [requests, setRequests] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  useEffect(() => {
    if (!propertyId || typeof propertyId !== "string" || propertyId.trim() === "") {
      console.warn("No valid propertyId received. Skipping API call.");
      setRequests([]);
      setLoading(false);
      return;
    }

    const trimmedPropertyId = propertyId.trim();
    fetchRequestsByMember(trimmedPropertyId);
  }, [propertyId]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoadingStatuses(true);
        const res = await api.get("/statuses/architectural");

        const map = {};
        (res.data || []).forEach((s) => {
          const key = String(s.statusname || "").toLowerCase().trim();
          map[key] = {
            colorKey: normalizePaletteKey(s.color, "secondary"),
            icon: s.icon,
          };
        });

        setStatusMap(map);
      } catch (err) {
        console.error("Failed to load architectural statuses", err);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [requests]);

  const fetchRequestsByMember = async (trimmedPropertyId) => {
    try {
      setLoading(true);

      const response = await api.get(
        `/architectural-requests/member/${trimmedPropertyId}`
      );

      if (Array.isArray(response.data)) {
        setRequests([...response.data]);
        setErrorMessage(null);
      } else {
        console.error("Unexpected API response format:", response.data);
        setRequests([]);
        setErrorMessage("Invalid data format received.");
      }
    } catch (error) {
      console.error("API Fetch Error:", error);
      setErrorMessage("Failed to load requests.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil((requests?.length || 0) / ITEMS_PER_PAGE);

  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return (requests || []).slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [requests, currentPage]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (loading || loadingStatuses) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {errorMessage && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: "12px",
          }}
        >
          {errorMessage}
        </Alert>
      )}

      {requests.length > 0 ? (
        <>
          <Stack spacing={2.25}>
            {paginatedRequests.map((request) => {
              const statusKey = String(request.status || "").toLowerCase().trim();
              const paletteKey = statusMap[statusKey]?.colorKey || "secondary";

              return (
                <Card
                  key={request.id}
                  sx={{
                    ...pageCardSx,
                    p: { xs: 1.5, sm: 2, md: 2.25 },
                    borderLeft: "6px solid",
                    borderLeftColor: (theme) => theme.palette[paletteKey].main,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                      gap: 2,
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(47,99,173,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <ArchitectureIcon sx={{ color: "#2f63ad" }} />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "#223142",
                              lineHeight: 1.2,
                            }}
                          >
                            REQ-{request.id}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6b778c",
                              mt: 0.25,
                              fontWeight: 600,
                            }}
                          >
                            {request.request_type_name ||
                              `Type ID: ${request.request_type}`}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0.75, sm: 2.5 }}
                        sx={{ pl: { xs: 0, sm: "52px" } }}
                      >
                        {request.subtype_name && (
                          <Typography variant="body2" sx={{ color: "#6b778c" }}>
                            <Box
                              component="span"
                              sx={{ fontWeight: 700, color: "#223142" }}
                            >
                              Subtype:
                            </Box>{" "}
                            {request.subtype_name}
                          </Typography>
                        )}

                        <Typography variant="body2" sx={{ color: "#6b778c" }}>
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, color: "#223142" }}
                          >
                            Requested:
                          </Box>{" "}
                          {formatDate(request.requested_at)}
                        </Typography>

                        <Typography variant="body2" sx={{ color: "#6b778c" }}>
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, color: "#223142" }}
                          >
                            Status:
                          </Box>{" "}
                          {request.status || "Unknown"}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: 1.25,
                        flexDirection: { xs: "column", sm: "row" },
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      <Chip
                        label={String(request.status || "UNKNOWN").toUpperCase()}
                        color={paletteKey}
                        sx={{
                          fontWeight: 700,
                          color: "#fff",
                          height: 32,
                          borderRadius: "999px",
                          "& .MuiChip-label": {
                            px: 1.25,
                          },
                        }}
                      />

                      <Button
                        component={Link}
                        to={`/home/architecturalrequest/${request.id}`}
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        sx={secondaryButtonSx}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Stack>

          {requests.length > ITEMS_PER_PAGE && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, requests.length)} of{" "}
                {requests.length} requests
              </Typography>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: "10px",
                    fontWeight: 600,
                  },
                }}
              />
            </Box>
          )}
        </>
      ) : (
        <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
            No architectural requests found
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
            This member does not currently have any recorded architectural requests.
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default ArchitecturalRequestByMember;